/**
 * HoneyCub Hero v2 — showcase slideshow.
 *
 * Differences from the standalone mockup script (which used bare
 * document.querySelectorAll and a single global timer):
 *   - Scoped per section instance, so multiple copies on one page work.
 *   - Re-initialises on Theme Editor section load/select events.
 *   - Pauses while off-screen and while the tab is hidden.
 *   - Respects prefers-reduced-motion (no autoplay; dots still work).
 *
 * No dependency on core theme JS.
 */

const SECTION_SELECTOR = '[data-hc-hero-v2]';

class HoneyCubHeroV2 {
  constructor(root) {
    this.root = root;
    this.slides = Array.from(root.querySelectorAll('[data-slide]'));
    this.dots = Array.from(root.querySelectorAll('[data-dot]'));
    this.current = 0;
    this.timer = null;

    if (this.slides.length === 0) return;

    this.speed = parseInt(root.dataset.autoplay, 10) || 4200;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.goTo(i);
        this.restart();
      });
    });

    // Pause when the section scrolls out of view.
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? this.start() : this.stop()));
        },
        { threshold: 0.15 }
      );
      this.observer.observe(root);
    } else {
      this.start();
    }

    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  goTo(index) {
    if (index === this.current || !this.slides[index]) return;

    this.slides[this.current]?.classList.remove('is-active');
    this.dots[this.current]?.classList.remove('is-active');

    this.current = index;

    this.slides[this.current]?.classList.add('is-active');
    this.dots[this.current]?.classList.add('is-active');
  }

  next() {
    this.goTo((this.current + 1) % this.slides.length);
  }

  start() {
    if (this.timer || this.slides.length < 2 || this.reducedMotion.matches) return;
    this.timer = setInterval(() => this.next(), this.speed);
  }

  stop() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }

  destroy() {
    this.stop();
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }
}

const instances = new WeakMap();

function init(scope = document) {
  const roots =
    scope.matches && scope.matches(SECTION_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(SECTION_SELECTOR));

  roots.forEach((root) => {
    if (instances.has(root)) return;
    instances.set(root, new HoneyCubHeroV2(root));
  });
}

function destroy(scope) {
  const roots =
    scope.matches && scope.matches(SECTION_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(SECTION_SELECTOR));

  roots.forEach((root) => {
    instances.get(root)?.destroy();
    instances.delete(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// Theme Editor lifecycle
document.addEventListener('shopify:section:load', (e) => {
  if (e.target instanceof HTMLElement) init(e.target);
});
document.addEventListener('shopify:section:unload', (e) => {
  if (e.target instanceof HTMLElement) destroy(e.target);
});